// Handoff helpers for launching the new (v2) ERP with the current session so
// users never have to log in again when they try the beta.

export const BETA_URL =
  process.env.REACT_APP_BETA_URL || "https://beta-v2.ekalsutra.com";

/** base64url-encode a JS object (unicode-safe) for the SSO hash param. */
function encodeSSO(obj) {
  const json = JSON.stringify(obj);
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Build the SSO payload from redux state + localStorage, matching the shapes
 * the v2 auth/school stores expect (`setSession` / `setSchoolAccess`).
 * Returns `null` if the essential auth token is missing.
 */
export function buildSSOUrl({
  userDocId,
  username,
  roles,
  selectedSchool,
  primaryRole,
  menuOptionsToShow,
  sessionList,
  activeSession,
}) {
  const authAccessToken = localStorage.getItem("authAccessToken");
  const authRefreshToken = localStorage.getItem("authRefreshToken");
  if (!authAccessToken) return null;

  const auth = {
    userDocId,
    username,
    roles: roles || [],
    token: authAccessToken,
    refreshToken: authRefreshToken,
  };

  let school;
  const schoolToken = localStorage.getItem("schoolToken");
  if (schoolToken && selectedSchool?._id) {
    const addr = selectedSchool.schoolAddress || {};
    const location = [addr.dist, addr.state, addr.pinCode]
      .filter(Boolean)
      .join(", ");
    school = {
      school: {
        _id: selectedSchool._id,
        name: selectedSchool.schoolName,
        schoolLogo: selectedSchool.schoolLogo,
        location: location || undefined,
        instituteType: selectedSchool.instituteType,
        activeSession: activeSession || selectedSchool.activeSession,
        sessionList: (sessionList || [])
          .map((s) => s?.sessionName || s)
          .filter(Boolean),
        primaryRole: primaryRole || selectedSchool.primaryRole,
      },
      schoolToken,
      schoolRefreshToken: localStorage.getItem("schoolRefreshToken"),
      menuOptionsToShow: menuOptionsToShow || {},
    };
  }

  const encoded = encodeSSO({ auth, school });
  return `${BETA_URL.replace(/\/$/, "")}/sso#d=${encoded}`;
}

/* -------------------------------------------------------------------------- */
/*  Beta awareness popup — "twice a day" gate                                  */
/* -------------------------------------------------------------------------- */

const PROMO_SHOWN_KEY = "betaPromoLastShownSlot";
const PROMO_MUTED_KEY = "betaPromoMuted";

/* The day is split into two showing slots, so a user sees the promo at most
   once in the morning and once from noon onwards — two times a day. */
const NOON_HOUR = 12;

/** Local (not UTC) YYYY-MM-DD, so "today" matches the user's own day. */
function todayKey() {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Which of the two slots the current local time falls in. */
function currentSlot() {
  return new Date().getHours() < NOON_HOUR ? "morning" : "noon";
}

/** e.g. "2026-09-08#morning" — the unit the "shown already" check works on. */
function currentSlotKey() {
  return `${todayKey()}#${currentSlot()}`;
}

/** Per-user keys so a shared machine doesn't hide the promo for everyone. */
function scopedKey(base, userDocId) {
  return userDocId ? `${base}:${userDocId}` : base;
}

/** True when the promo has not been shown yet in this slot and isn't muted. */
export function shouldShowBetaPromo(userDocId) {
  try {
    if (localStorage.getItem(scopedKey(PROMO_MUTED_KEY, userDocId)) === "true")
      return false;
    return (
      localStorage.getItem(scopedKey(PROMO_SHOWN_KEY, userDocId)) !==
      currentSlotKey()
    );
  } catch (e) {
    // Private mode / storage blocked — don't nag in a loop.
    return false;
  }
}

/** Remember that this slot's popup has been served. */
export function markBetaPromoShown(userDocId) {
  try {
    localStorage.setItem(scopedKey(PROMO_SHOWN_KEY, userDocId), currentSlotKey());
  } catch (e) {
    /* ignore */
  }
}

/** "Don't show this again" — permanently opt out of the popup. */
export function muteBetaPromo(userDocId) {
  try {
    localStorage.setItem(scopedKey(PROMO_MUTED_KEY, userDocId), "true");
  } catch (e) {
    /* ignore */
  }
}
